import {
  Alert,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { PageContainer } from '@tiles-erp/ui';
import type { SettingItem } from '@tiles-erp/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api-client';
import { brandingQueryKey } from '../../app/branding';
import { LogoSetting } from './LogoSetting';
import { useSettings, useUpdateSetting } from './api';

/** Settings the shell reads through its own cached branding query. */
const BRANDING_KEYS = new Set(['app.name', 'app.logo']);

function SettingRow({ setting }: { setting: SettingItem }): JSX.Element {
  const updateSetting = useUpdateSetting();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(setting.value);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(setting.value);
  }, [setting.value]);

  const dirty = value !== setting.value;
  const isBoolean = [
    'inventory.allowNegativeBilling',
    'quotation.showDiscountPercentage',
    'quotation.showMrp',
  ].includes(setting.key);

  const save = async (): Promise<void> => {
    setError(null);
    setSaved(false);
    try {
      await updateSetting.mutateAsync({ key: setting.key, value, version: setting.version });
      // The sidebar, browser tab and print headers read the name from the branding
      // query, not from this list — without this, saving app.name changed nothing
      // visible until a reload.
      if (BRANDING_KEYS.has(setting.key)) {
        await queryClient.invalidateQueries({ queryKey: brandingQueryKey });
      }
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save');
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">{setting.key}</Typography>
            {saved && !dirty && (
              <Typography variant="caption" color="success.main">
                Saved
              </Typography>
            )}
          </Stack>
          {setting.description && (
            <Typography variant="body2" color="text.secondary">
              {setting.description}
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isBoolean ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={value === 'true'}
                    onChange={(_, checked) => {
                      setValue(String(checked));
                      setSaved(false);
                    }}
                  />
                }
                label={value === 'true' ? 'Enabled' : 'Disabled'}
              />
            ) : (
              <TextField
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSaved(false);
                }}
                size="small"
                multiline
                maxRows={8}
              />
            )}
            <Button
              variant="contained"
              size="small"
              disabled={!dirty || updateSetting.isPending}
              onClick={() => void save()}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function SettingsPage(): JSX.Element {
  const { data, isLoading } = useSettings();

  return (
    <PageContainer title="Settings" subtitle="Application-wide configuration.">
      <Stack spacing={2} sx={{ maxWidth: 640 }}>
        {isLoading &&
          [1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={120} />)}
        {(data ?? []).map((setting) =>
          // The logo is an image, not a string anyone should be pasting into a
          // text field — it gets a picker with a preview instead.
          setting.key === 'app.logo' ? (
            <LogoSetting key={setting.key} setting={setting} />
          ) : (
            <SettingRow key={setting.key} setting={setting} />
          ),
        )}
      </Stack>
    </PageContainer>
  );
}
